import { useState, useEffect } from 'react'
import { ResponsiveBar } from '@nivo/bar'
import s from './PipelineByUser.scss'
import { makeReportsPostRequest, makeGetRequest, getReportsBaseUrl } from 'utils/api'
import { toTeams } from 'utils/serializers'
import { toBarChartData, toTable, toUsers } from './serializers'
import { useLoading } from 'hooks'
import { Col, Row, UncontrolledTooltip } from 'reactstrap'
import { getCurrency, getToken } from 'utils/authService'
import MaterialTable from '@material-table/core'
import { NumericFormat } from 'react-number-format'
import { RangeDatePicker } from 'components/ideal-core-ui'
import { utcDate, currencyFormatter } from 'utils/helpers'
import isEmpty from 'lodash/isEmpty'
import cn from 'classnames'
import Select from 'react-select'
import { FaInfoCircle, FaFileExcel } from 'react-icons/fa'
import Cookies from 'universal-cookie'
import { COOKIES } from 'utils/constants'
import moment from 'moment'
import { AddButton } from 'components/common/AddButton'
import { ExportCsv, ExportPdf } from '@material-table/exporters'

const columns = [
  { title: 'User', field: 'user', sorting: true },
  { title: 'Count', field: 'count', sorting: true },
  {
    title: 'Value',
    field: 'value',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.value}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.value })
  },
  {
    title: 'Percent of Total',
    field: 'valuePercentage',
    sorting: true,
    render: row => `${row.valuePercentage}%`,
    exportTransformer: row => `${row.valuePercentage}%`
  }
]

const cookies = new Cookies()

const PipelineByUser = () => {
  const [pipelineInfo, setPipelineInfo] = useState(null)
  const [tableInfo, setTableInfo] = useState([])
  const [result, setResult] = useState([])
  const [users, setUsers] = useState([])
  const [userFilter, setUserFilter] = useState([])
  const [teamFilter, setTeamFilter] = useState([])
  const [teams, setTeams] = useState([])
  const [, withLoadingLocks] = useLoading()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [type, setType] = useState('value')

  const setDateFilters = (dates, utc = true) => {
    setStartDate(utc ? utcDate(dates.from) : dates.from)
    setEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  useEffect(() => {
    const sessionDateRangeFilters = cookies.get(COOKIES.DATE_RANGE_FILTERS)
    if (sessionDateRangeFilters?.startDate) {
      setDateFilters(
        {
          from: sessionDateRangeFilters.startDate,
          to: sessionDateRangeFilters.endDate
        },
        false
      )
    } else {
      setDateFilters({
        from: moment().subtract(2, 'weeks').startOf('day').format(),
        to: moment().add(6, 'weeks').endOf('day').format()
      })
    }
  }, [])

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      const teams = await makeGetRequest('/v1/teams')
      setTeams(toTeams(teams))
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      if (!startDate || !endDate) return
      const result = await withLoadingLocks(
        makeReportsPostRequest('/v1/reports/pipeline_user', {
          from: startDate,
          to: endDate,
          ...(userFilter && userFilter.length
            ? { userFilter: userFilter.map(user => user.value) }
            : {}),
          ...(teamFilter && teamFilter.length
            ? { teamFilter: teamFilter.map(user => user.value) }
            : {})
        })
      )
      setResult(result)
      setPipelineInfo(toBarChartData(result.pipelineInfo))
      setTableInfo(toTable(result.pipelineInfo))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, teamFilter, userFilter])

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/reports/pipeline_user/export?from=${startDate}&to=${endDate}&userFilter=${userFilter
          .map(user => user.value)
          .join()}&teamFilter=${teamFilter.map(user => user.value).join()}&token=${getToken()}`
      ),
      '_blank'
    )
  }

  return (
    <>
      <h6 className={s.title}>Team Sales Reports &gt; Pipeline by User</h6>
      <div className={s.filters}>
        <div
          onClick={() => setType('count')}
          className={cn(s.toggler, {
            [s.active]: type === 'count'
          })}
        >
          Count
        </div>
        <div
          onClick={() => setType('value')}
          className={cn(s.toggler, {
            [s.active]: type === 'value'
          })}
        >
          Value
        </div>
        <RangeDatePicker startDate={startDate} endDate={endDate} onChange={setDateFilters} />
        <br />
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type1" style={{ position: 'relative', right: 10 }} />
        <UncontrolledTooltip placement="bottom" target="Type1">
          To see all opportunities, please remove all users from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setTeamFilter(values)}
          options={teams}
          value={teamFilter}
          placeholder="Select Team(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type2" style={{ position: 'relative', right: 10 }} />
        <UncontrolledTooltip placement="bottom" target="Type2">
          To see all opportunities, please remove all teams from selection.
        </UncontrolledTooltip>
      </div>
      {result.pipelineInfo?.length > 0 && !isEmpty(pipelineInfo) ? (
        <>
          <Row>
            <Col lg="1">
              <div className={s.info}>
                <p>Total Pipeline: </p>
                <p>
                  <NumericFormat
                    thousandSeparator
                    decimalScale={2}
                    value={pipelineInfo.totalValue}
                    prefix={getCurrency().symbol}
                    displayType="text"
                  />
                </p>
                <p>Opportunities: {pipelineInfo.totalCount}</p>
              </div>
            </Col>
            <Col lg="11">
              <div className={s.totalChart}>
                <ResponsiveBar
                  data={[
                    type === 'value'
                      ? pipelineInfo.stackedBarByValue
                      : pipelineInfo.stackedBarByCount
                  ]}
                  keys={pipelineInfo.users}
                  margin={{ top: 0, right: 130, bottom: 50, left: 60 }}
                  padding={0.15}
                  layout="horizontal"
                  colors={{ scheme: 'nivo' }}
                  borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={null}
                  axisLeft={null}
                  tooltip={({ id, value }) => (
                    <span>
                      {id}:{' '}
                      <strong>
                        {type === 'value' ? (
                          <NumericFormat
                            thousandSeparator
                            decimalScale={2}
                            value={value}
                            prefix={getCurrency().symbol}
                            displayType="text"
                          />
                        ) : (
                          value
                        )}
                      </strong>
                    </span>
                  )}
                  enableGridY={false}
                  enableLabel={false}
                  labelSkipWidth={12}
                  labelSkipHeight={9}
                  labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  animate
                  motionStiffness={90}
                  motionDamping={15}
                />
              </div>
            </Col>
          </Row>
          <div
            className={s.barChart}
            style={{ height: pipelineInfo.barByValue.length * 60 + 'px', maxHeight: 800 }}
          >
            <ResponsiveBar
              data={type === 'value' ? pipelineInfo.barByValue : pipelineInfo.barByCount}
              keys={pipelineInfo.keys}
              indexBy="user"
              margin={{ top: 0, right: 200, bottom: 50, left: 170 }}
              padding={0.45}
              layout="horizontal"
              colors={{ scheme: 'nivo' }}
              borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: 'middle',
                legendOffset: 32
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: 'middle',
                legendOffset: -40
              }}
              tooltip={({ id, value }) => (
                <span>
                  {id}:{' '}
                  <strong>
                    {type === 'value' ? (
                      <NumericFormat
                        thousandSeparator
                        decimalScale={2}
                        value={value}
                        prefix={getCurrency().symbol}
                        displayType="text"
                      />
                    ) : (
                      value
                    )}
                  </strong>
                </span>
              )}
              enableGridY
              enableGridX
              enableLabel={false}
              labelSkipWidth={12}
              labelSkipHeight={9}
              labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'bottom-right',
                  direction: 'column',
                  justify: false,
                  translateX: 120,
                  translateY: 0,
                  itemsSpacing: 14,
                  itemWidth: 100,
                  itemHeight: 20,
                  itemDirection: 'left-to-right',
                  itemOpacity: 0.85,
                  symbolSize: 20,
                  toggleSerie: true,
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemOpacity: 1
                      }
                    }
                  ]
                }
              ]}
              animate
              motionStiffness={90}
              motionDamping={15}
            />
          </div>
          <div className={s.table}>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
            <MaterialTable
              title=""
              columns={columns}
              data={[
                {
                  count: pipelineInfo.totalCount,
                  user: 'All',
                  value: pipelineInfo.totalValue,
                  valuePercentage: 100
                },
                ...tableInfo
              ]}
              options={{
                actionsColumnIndex: -1,
                sorting: true,
                headerStyle: {
                  backgroundColor: '#eee',
                  color: '#000',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  fontSize: '14px'
                },
                maxBodyHeight: '230px',
                pageSize: 20,
                padding: 'dense',
                pageSizeOptions: [
                  5,
                  10,
                  20,
                  50,
                  {
                    value: [
                      {
                        count: pipelineInfo.totalCount,
                        user: 'All',
                        value: pipelineInfo.totalValue,
                        valuePercentage: 100
                      },
                      ...tableInfo
                    ].length,
                    label: 'All'
                  }
                ],
                emptyRowsWhenPaging: false,
                exportMenu: [
                  {
                    label: 'Export PDF',
                    exportFunc: (cols, datas) => ExportPdf(cols, datas, 'Pipeline by Stage')
                  },
                  {
                    label: 'Export CSV',
                    exportFunc: (cols, datas) => ExportCsv(cols, datas, 'Pipeline by Stage')
                  }
                ],
                columnsButton: true
              }}
              style={{
                boxShadow: 'none',
                fontSize: '14px'
              }}
            />
          </div>
        </>
      ) : (
        <p className={s.noResults}>No results to display. Try different filters.</p>
      )}
    </>
  )
}

export default PipelineByUser
